/*
* Copyright (C) GMS Inc - All Rights Reserved
* This file is part of GMSCloud. - Proprietary and confidential
* Unauthorized copying of this file, via any medium is strictly prohibited
*
* Created on   : 18/02/26
* @author      : Pavithra P
* @since       : 0.0.1
* Requirement# :
* Purpose      :
* -----------------------------------------------------------------------------
* Revision History
* -----------------------------------------------------------------------------
* Requirement/
* Issue/WorkItem |   DATE      |   AUTHOR  |   DESCRIPTION OF CHANGE
* -----------------------------------------------------------------------------
* 28849          |  28/08/26   |   surya   |  Fixed pagination only ever showing the
*                                           |  backend's first page of 10 records —
*                                           |  fetchRows now probes the true total and
*                                           |  loads every manufacturer, with stale-fetch
*                                           |  and partial-failure guards.
*
*/

import * as React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Chip,
  Skeleton,
  Avatar,
  alpha,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHandleUnauthorized } from '../userSession/UserSession';
import { getManufacturerTableData, deleteManufacturer } from '../services/ManufacturerService';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import ManufacturerRegister from '../pages/Masters/ManufacturerRegister';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Order = 'asc' | 'desc';

export interface ManufacturerRow {
  id: number;
  name: string;
  email: string;
  phoneNo: string;
  status: 'Active' | 'Inactive' | string;
}

type SortableKey = keyof ManufacturerRow;

interface HeadCell {
  id: SortableKey;
  label: string;
  numeric: boolean;
  minWidth?: number;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const HEAD_CELLS: readonly HeadCell[] = [
  { id: 'name', label: 'Manufacturer', numeric: false, minWidth: 200 },
  { id: 'email', label: 'Email', numeric: false, minWidth: 200 },
  { id: 'phoneNo', label: 'Phone', numeric: false, minWidth: 140 },
  { id: 'status', label: 'Status', numeric: false, minWidth: 100 },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// Hard ceiling on the "fetch everything" page request below — the backend endpoint has no
// max-size clamp of its own (it builds PageRequest.of() directly from the query param).
const MAX_MANUFACTURER_PAGE_SIZE = 5000;

// ─── Utilities ─────────────────────────────────────────────────────────────────

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: Record<Key, any>, b: Record<Key, any>) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function getInitials(name: string): string {
  if (!name) return 'UN';
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function stringToHue(str: string): number {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}

/** Pulls the manufacturer array out of whichever response envelope shape the API returned. */
function extractManufacturers(response: any): any[] {
  const apiData = response?.data;
  if (apiData?.content && Array.isArray(apiData.content)) return apiData.content;
  if (apiData?.data && Array.isArray(apiData.data)) return apiData.data;
  if (apiData && Array.isArray(apiData)) return apiData;
  if (Array.isArray(response)) return response;
  return [];
}

// ─── Sub-components ────────────────────────────────────────────────────────────

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: SortableKey;
  onRequestSort: (event: React.MouseEvent<unknown>, property: SortableKey) => void;
}

const EnhancedTableHead: React.FC<EnhancedTableHeadProps> = ({ order, orderBy, onRequestSort }) => (
  <TableHead>
    <TableRow>
      {HEAD_CELLS.map((cell) => (
        <TableCell
          key={cell.id}
          sortDirection={orderBy === cell.id ? order : false}
          sx={{
            minWidth: cell.minWidth,
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            backgroundColor: (theme) => theme.palette.grey[50],
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            py: 1.5,
            whiteSpace: 'nowrap',
          }}
        >
          <TableSortLabel
            active={orderBy === cell.id}
            direction={orderBy === cell.id ? order : 'asc'}
            onClick={(e) => onRequestSort(e, cell.id)}
            sx={{
              '&.Mui-active': { color: 'primary.main' },
              '&:hover': { color: 'primary.main' },
            }}
          >
            {cell.label}
            {orderBy === cell.id && (
              <Box component="span" sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            )}
          </TableSortLabel>
        </TableCell>
      ))}
      <TableCell
        align="center"
        sx={{
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          backgroundColor: (theme) => theme.palette.grey[50],
          borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
          py: 1.5,
        }}
      >
        Actions
      </TableCell>
    </TableRow>
  </TableHead>
);

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={140} height={20} />
          </Stack>
        </TableCell>
        {['email', 'phoneNo', 'status'].map((k) => (
          <TableCell key={k}>
            <Skeleton variant="text" width="70%" height={20} />
          </TableCell>
        ))}
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center">
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </TableCell>
      </TableRow>
    ))}
  </>
);

const EmptyState: React.FC<{ hasSearch: boolean; loadFailed: boolean }> = ({ hasSearch, loadFailed }) => (
  <TableRow>
    <TableCell colSpan={HEAD_CELLS.length + 1}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={1.5}>
        {loadFailed ? (
          <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: 'error.main' }} />
        ) : (
          <PrecisionManufacturingRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        )}
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          {loadFailed ? 'Couldn\'t load manufacturers' : hasSearch ? 'No results found' : 'No manufacturers yet'}
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={300}>
          {loadFailed
            ? 'This is not necessarily an empty list — retry to check the real count.'
            : hasSearch
              ? 'Try adjusting your search or clear the filter to see all manufacturers.'
              : 'Get started by adding your first manufacturer.'}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const isActive = status?.toLowerCase() === 'active';
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        height: 22,
        borderRadius: 1,
        backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
        color: isActive ? '#2e7d32' : '#757575',
        border: 'none',
      }}
    />
  );
};

// ─── Snackbar state helper ──────────────────────────────────────────────────────

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const SNACKBAR_CLOSED: SnackbarState = { open: false, message: '', severity: 'success' };

// ─── Main Component ────────────────────────────────────────────────────────────

const ManufacturerList: React.FC = () => {
  const { accessToken } = useOidcAccessToken();
  const { handleUnauthorized } = useHandleUnauthorized();

  const [rows, setRows] = useState<ManufacturerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>(SNACKBAR_CLOSED);
  const [openRegister, setOpenRegister] = useState(false);
  const [editRow, setEditRow] = useState<ManufacturerRow | undefined>(undefined);

  // ── Data fetching ──

  // Guards against a stale response landing after a newer fetch (or a local mutation like
  // delete) already updated `rows` — see fetchRows below.
  const fetchSeqRef = useRef(0);

  // Stable fetchRows: only depends on accessToken value
  const fetchRows = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      // The backend paginates (default page size 10). A minimal probe call reads the true
      // record count from `totalElements`, then — only if the probe didn't already return
      // everything — a second call asks for the rest in one page. This keeps the UI's
      // client-side sort/search/pagination working over the full dataset instead of only
      // whatever the backend's own default page size happened to be.
      const probe = await getManufacturerTableData(accessToken, 0, 1, 'ASC');
      handleUnauthorized(probe);
      if (!probe?.ok) throw new Error(probe?.message || 'Failed to load manufacturers.');

      let response = probe;
      let manufacturers = extractManufacturers(probe);
      const totalElements = probe?.data?.totalElements;
      if (typeof totalElements === 'number' && totalElements > manufacturers.length) {
        const size = Math.min(totalElements, MAX_MANUFACTURER_PAGE_SIZE);
        response = await getManufacturerTableData(accessToken, 0, size, 'ASC');
        handleUnauthorized(response);
        if (!response?.ok) throw new Error(response?.message || 'Failed to load manufacturers.');
        manufacturers = extractManufacturers(response);
      }

      if (fetchSeqRef.current !== seq) return; // a newer fetch already landed

      const mappedData = manufacturers.map((item: any) => ({
        ...item,
        name: item.name || item.displayName || 'Unnamed',
        phoneNo: item.phoneNo || item.phone || item.primaryMobileNumber || ''
      }));
      setRows(mappedData);
    } catch {
      if (fetchSeqRef.current !== seq) return;
      setLoadFailed(true);
      setSnackbar({ open: true, message: 'Failed to load manufacturers.', severity: 'error' });
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchRows();
    // Only run when accessToken changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ── Sorting & filtering ──

  const handleRequestSort = useCallback(
    (_event: React.MouseEvent<unknown>, property: SortableKey) => {
      setOrder((prev) => (orderBy === property && prev === 'asc' ? 'desc' : 'asc'));
      setOrderBy(property);
    },
    [orderBy],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.phoneNo?.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  // Loading more than the backend's old fixed page of 10 means a delete/search can now
  // routinely leave `page` pointing past the last page — clamp it back into range.
  useEffect(() => {
    setPage((prev) => {
      const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
      return prev > maxPage ? maxPage : prev;
    });
  }, [filteredRows.length, rowsPerPage]);

  const visibleRows = useMemo(
    () =>
      [...filteredRows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, order, orderBy, page, rowsPerPage],
  );

  // ── Register dialog ──

  const handleAdd = () => {
    setEditRow(undefined);
    setOpenRegister(true);
  };

  const handleEdit = (row: ManufacturerRow) => {
    setEditRow(row);
    setOpenRegister(true);
  };

  const handleAfterSave = () => {
    setOpenRegister(false);
    fetchRows();
  };

  // ── Delete flow ──

  const openDeleteDialog = (id: number) => setDeleteTargetId(id);
  const closeDeleteDialog = () => { if (!isDeleting) setDeleteTargetId(null); };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) return;
    setIsDeleting(true);
    try {
      const result = await deleteManufacturer(deleteTargetId, accessToken);
      handleUnauthorized(result);
      if (result.status === 200 || result.status === 204) {
        setRows((prev) => prev.filter((r) => r.id !== deleteTargetId));
        setSnackbar({ open: true, message: 'Manufacturer deleted successfully.', severity: 'success' });
      } else {
        throw new Error('Unexpected status');
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete manufacturer. Please try again.', severity: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteTargetId(null);
    }
  };

  const closeSnackbar = () => setSnackbar(SNACKBAR_CLOSED);

  // ── Render ──

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* ── Toolbar ── */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          px={3}
          py={2.5}
          gap={2}
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1}>
              Manufacturers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loading ? 'Loading…' : (
                <>
                  {filteredRows.length} {filteredRows.length === 1 ? 'record' : 'records'}
                  {searchQuery && ` matching "${searchQuery}"`}
                </>
              )}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <TextField
              size="small"
              placeholder="Search by name, email or phone…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' },
              }}
            />
            <Button
              onClick={handleAdd}
              variant="contained"
              disableElevation
              startIcon={<AddRoundedIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              Add Manufacturer
            </Button>
          </Stack>
        </Box>

        {/* ── Table ── */}
        <TableContainer sx={{ maxHeight: 800 }}>
          <Table stickyHeader size="medium">
            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {loading ? (
                <SkeletonRows count={rowsPerPage} />
              ) : visibleRows.length === 0 ? (
                <EmptyState hasSearch={!!searchQuery} loadFailed={loadFailed} />
              ) : (
                visibleRows.map((row) => {
                  const hue = stringToHue(row.name);
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:last-child td': { border: 0 },
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {/* Name + avatar */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              backgroundColor: `hsl(${hue}, 55%, 88%)`,
                              color: `hsl(${hue}, 55%, 30%)`,
                            }}
                          >
                            {getInitials(row.name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {row.email}
                        </Typography>
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.phoneNo}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit manufacturer" arrow>
                            <IconButton
                              size="small"
                              id={`manufacturerList_editButton_${row.id}`}
                              onClick={() => handleEdit(row)}
                              aria-label={`Edit ${row.name}`}
                              sx={{
                                color: 'primary.main',
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1) },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete manufacturer" arrow>
                            <IconButton
                              size="small"
                              id={`manufacturerList_deleteButton_${row.id}`}
                              onClick={() => openDeleteDialog(row.id)}
                              aria-label={`Delete ${row.name}`}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1) },
                              }}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
        />
      </Paper>

      {/* ── Register / Edit Dialog ── */}
      {openRegister && (
        <ManufacturerRegister
          open={openRegister}
          onClose={() => setOpenRegister(false)}
          rowData={editRow}
          afterSave={handleAfterSave}
        />
      )}

      {/* ── Delete Confirmation ── */}
      <Dialog
        id="manufacturerList_deleteDialog"
        open={deleteTargetId !== null}
        onClose={closeDeleteDialog}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            maxWidth: 380,
            width: '100%',
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 32, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Delete Manufacturer?
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            Manufacturer may be associated with material. Confirm that manufacturer is associated with material.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            id="manufacturerList_cancelDeleteButton"
            onClick={closeDeleteDialog}
            disabled={isDeleting}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            id="manufacturerList_confirmDeleteButton"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            variant="contained"
            color="error"
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feedback Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManufacturerList;